import { createContextTools } from "@orgiggly/shared";
import { useMemo } from "react";

export type Params = {
  orgName: string;
  isAdmin: boolean;
  username?: string;
  qualifiedOrgId?: string;
  variant?: string;
  embedMode?: boolean;
  // When the URL carries `?preview=draft`, the Site app loads the org's
  // server draft directly (bypassing the local-draft → published preference)
  // so a draft can be reviewed via a plain link. Distinct from the design-time
  // `usePreviewMode()` / `PreviewModeProvider`.
  previewDraft?: boolean;
};

// Fixed admin sub-routes that match `/admin/<segment>` but are not
// `{username}/{orgName}`. These render dedicated screens (Stripe redirects)
// and must keep returning empty orgName so the URL-driven org-load hook
// doesn't try to fetch them as an org.
const FIXED_ADMIN_PATHS = new Set([
  "/admin",
  "/admin/credits/success",
  "/admin/credits/cancel",
  "/admin/subscribe/success",
  "/admin/subscribe/cancel",
]);

export const parseParams = (pathname: string, search = ""): Params => {
  // `?preview=draft` is a Site-app cross-cutting flag, independent of which
  // path branch we land on, so resolve it once and merge it into the result.
  // Omitted when absent (like `embedMode`/`variant`) rather than set false.
  const previewDraft = new URLSearchParams(search).get("preview") === "draft";
  const base = parsePathParams(pathname, search);
  return previewDraft ? { ...base, previewDraft } : base;
};

const parsePathParams = (pathname: string, search: string): Params => {
  // Strip /embed/ prefix first. Everything else parses against the
  // stripped path — single parser, no duplication. Bare /embed and
  // /embed/ both fall through to the site-root default ("orgiggly").
  if (pathname === "/embed" || pathname === "/embed/") {
    return { isAdmin: false, embedMode: true, orgName: "orgiggly" };
  }
  if (pathname.startsWith("/embed/")) {
    const inner = parsePathParams(pathname.slice("/embed".length), search);
    return { ...inner, embedMode: true };
  }

  const isAdmin = pathname.startsWith("/admin");

  if (isAdmin) {
    if (FIXED_ADMIN_PATHS.has(pathname)) {
      return { isAdmin, orgName: "" };
    }

    // Strip leading "/admin/" and split. Two segments → {username}/{orgName}.
    // Anything else (one segment, or three+) is treated as a fixed path we
    // don't recognise — fall through to empty so OrgChooser handles it.
    const tail = pathname.slice("/admin/".length);
    const segments = tail.split("/").filter(Boolean);
    if (segments.length === 2) {
      const [username, orgName] = segments;
      return {
        isAdmin,
        orgName,
        username,
        qualifiedOrgId: `${username}/${orgName}`,
      };
    }
    return { isAdmin, orgName: "" };
  }

  // Site app: parse /{username}/{orgName}/{variant?} or /{singleSegment}
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length >= 3) {
    const [username, orgName, variant] = segments;
    const qualifiedOrgId = `${username}/${orgName}`;
    return { isAdmin, orgName, username, qualifiedOrgId, variant };
  }
  if (segments.length === 2) {
    const [username, orgName] = segments;
    const qualifiedOrgId = `${username}/${orgName}`;
    return { isAdmin, orgName, username, qualifiedOrgId };
  }
  if (segments.length === 1) {
    // Single segment: could be a username, legacy org, or landing page
    // Resolution happens in SiteApp; for now just pass as orgName
    return { isAdmin, orgName: segments[0] };
  }

  // Root "/" — default org
  return { isAdmin, orgName: "orgiggly" };
};

/**
 * Re-points site params parsed from an alias URL at the alias's `target`
 * (`username/orgName`), URL unchanged. From `/{alias}/{variant}` the parser
 * put the alias in `username` and the variant in `orgName`, so the variant
 * moves across; anything past it is ignored. A bare `/{alias}` (no
 * `username`) has no variant. Cross-cutting flags (`previewDraft`,
 * `embedMode`) survive the spread. (#2255)
 */
export const applyAliasTarget = (params: Params, target: string): Params => {
  const [username, orgName] = target.split("/");
  const { variant: _ignored, ...rest } = params;
  void _ignored;
  const aliased: Params = {
    ...rest,
    username,
    orgName,
    qualifiedOrgId: target,
  };
  return params.username ? { ...aliased, variant: params.orgName } : aliased;
};

export const useCreateParams = (): Params => {
  return useMemo(
    () => parseParams(window.location.pathname, window.location.search),
    [],
  );
};

export const [ParamsProvider, useParams] = createContextTools<Params>("params");
